import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Clock, Users, AlertCircle, CheckCircle, Pause, RefreshCw, Filter, Eye, EyeOff } from 'lucide-react';

const LiveMap = ({ hideActiveTrips = false }) => {
  const [activeTab, setActiveTab] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [mapView, setMapView] = useState('satellite');

  const tabs = [
    { id: 'in_transit', label: 'In Transit', count: 3, color: 'bg-red-500' },
    { id: 'enroute', label: 'Enroute', count: 2, color: 'bg-blue-500' },
    { id: 'idle', label: 'Idle', count: 1, color: 'bg-gray-500' }
  ];

  // Mock real-time trip data
  const trips = [
    {
      id: 'TRIP-001',
      ambulanceId: 'AMB-001',
      status: 'in_transit',
      driver: 'Dr. Rajesh Kumar',
      patient: 'John Doe',
      pickupLocation: 'Connaught Place',
      destination: 'Apollo Hospital',
      currentLocation: { lat: 28.6139, lng: 77.2090 },
      eta: '8 mins',
      distance: '2.3 km',
      priority: 'high',
      startTime: '14:30',
      lastUpdate: '2 mins ago',
      patients: 1
    },
    {
      id: 'TRIP-002',
      ambulanceId: 'AMB-002',
      status: 'in_transit',
      driver: 'Dr. Priya Sharma',
      patient: 'Sarah Wilson',
      pickupLocation: 'Karol Bagh',
      destination: 'Fortis Hospital',
      currentLocation: { lat: 28.6145, lng: 77.2105 },
      eta: '12 mins',
      distance: '4.1 km',
      priority: 'medium',
      startTime: '14:25',
      lastUpdate: '1 min ago',
      patients: 1
    },
    {
      id: 'TRIP-003',
      ambulanceId: 'AMB-003',
      status: 'in_transit',
      driver: 'Dr. Amit Singh',
      patient: 'Mike Johnson',
      pickupLocation: 'Lajpat Nagar',
      destination: 'Max Hospital',
      currentLocation: { lat: 28.6120, lng: 77.2075 },
      eta: '15 mins',
      distance: '5.7 km',
      priority: 'high',
      startTime: '14:20',
      lastUpdate: '3 mins ago',
      patients: 2
    },
    {
      id: 'TRIP-004',
      ambulanceId: 'AMB-004',
      status: 'enroute',
      driver: 'Dr. Sunita Patel',
      patient: 'Emergency Call',
      pickupLocation: 'Hospital Base',
      destination: 'Gurgaon',
      currentLocation: { lat: 28.6150, lng: 77.2110 },
      eta: '6 mins',
      distance: '1.8 km',
      priority: 'high',
      startTime: '14:35',
      lastUpdate: '1 min ago',
      patients: 0
    },
    {
      id: 'TRIP-005',
      ambulanceId: 'AMB-005',
      status: 'enroute',
      driver: 'Dr. Vikram Reddy',
      patient: 'Routine Transfer',
      pickupLocation: 'Hospital Base',
      destination: 'AIIMS',
      currentLocation: { lat: 28.6115, lng: 77.2085 },
      eta: '10 mins',
      distance: '3.2 km',
      priority: 'medium',
      startTime: '14:32',
      lastUpdate: '2 mins ago',
      patients: 0
    },
    {
      id: 'TRIP-006',
      ambulanceId: 'AMB-006',
      status: 'idle',
      driver: 'Dr. Neha Gupta',
      patient: 'Standby',
      pickupLocation: 'Hospital Base',
      destination: 'Available',
      currentLocation: { lat: 28.6100, lng: 77.2060 },
      eta: 'Available',
      distance: '0 km',
      priority: 'low',
      startTime: '14:00',
      lastUpdate: '5 mins ago',
      patients: 0
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_transit': return 'bg-red-500';
      case 'enroute': return 'bg-blue-500';
      case 'idle': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_transit': return <Users className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'enroute': return <Navigation className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'idle': return <Pause className="w-3 h-3 sm:w-4 sm:h-4" />;
      default: return <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const filteredTrips = activeTab ? trips.filter(trip => trip.status === activeTab) : trips;

  // Simulate real-time updates
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
        // In a real app, this would fetch updated trip data from the API
        console.log('Updating trip data...');
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isLive]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900">
              Live Map Monitor
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Monitor active trips and ETAs in real-time
            </p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="text-xs sm:text-sm text-gray-600">
                {isLive ? 'Live' : 'Paused'}
              </span>
            </div>
            
            <button
              onClick={() => setIsLive(!isLive)}
              className={`px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm rounded-lg transition-colors ${
                isLive 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {isLive ? <EyeOff className="w-3 h-3 sm:w-4 sm:h-4" /> : <Eye className="w-3 h-3 sm:w-4 sm:h-4" />}
            </button>
            
            <button className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(activeTab === tab.id ? '' : tab.id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-sm'
              }`}
            >
              <div className={`w-2 h-2 sm:w-3 sm:h-3 ${tab.color} rounded-full`}></div>
              <span>{tab.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-blue-500' : 'bg-gray-200'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Map and Trip List */}
        <div className={`grid grid-cols-1 gap-4 sm:gap-6 ${hideActiveTrips ? 'lg:grid-cols-1' : 'lg:grid-cols-3'}`}>
          {/* Map Container */}
          <div className={hideActiveTrips ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Map Controls */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <select
                    value={mapView}
                    onChange={(e) => setMapView(e.target.value)}
                    className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="satellite">Satellite</option>
                    <option value="street">Street</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                  
                  <span className="text-xs sm:text-sm text-gray-500">
                    Last update: {formatTime(lastUpdate)}
                  </span>
                </div>
                
                <div className="flex gap-1 sm:gap-2">
                  <button className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <span className="text-sm sm:text-base">+</span>
                  </button>
                  <button className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <span className="text-sm sm:text-base">−</span>
                  </button>
                </div>
              </div>

              {/* Map Area */}
              <div className="relative h-64 sm:h-80 lg:h-96 xl:h-[28rem] bg-gradient-to-br from-blue-50 to-green-50">
                {/* Mock Map Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-white to-green-100 opacity-50"></div>
                
                {/* Mock Roads */}
                <svg className="absolute inset-0 w-full h-full">
                  <path
                    d="M0 120 Q200 100 400 120 T800 120"
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M0 200 Q200 180 400 200 T800 200"
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M200 0 Q200 100 200 200 T200 400"
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M400 0 Q400 100 400 200 T400 400"
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M600 0 Q600 100 600 200 T600 400"
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.6"
                  />
                </svg>

                {/* Trip Markers */}
                {filteredTrips.map((trip, index) => (
                  <div
                    key={trip.id}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 hover:scale-110 ${
                      selectedTrip?.id === trip.id ? 'z-10' : 'z-5'
                    }`}
                    style={{
                      left: `${20 + (index * 15) % 60}%`,
                      top: `${30 + (index * 20) % 50}%`
                    }}
                    onClick={() => setSelectedTrip(trip)}
                  >
                    <div className={`relative ${getStatusColor(trip.status)} rounded-full p-2 sm:p-3 shadow-lg border-2 border-white`}>
                      {getStatusIcon(trip.status)}
                      
                      {/* Pulse animation for active trips */}
                      {(trip.status === 'in_transit' || trip.status === 'enroute') && (
                        <div className={`absolute inset-0 ${getStatusColor(trip.status)} rounded-full animate-ping opacity-75`}></div>
                      )}
                    </div>
                    
                    {/* Trip ID */}
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white px-1 sm:px-2 py-0.5 rounded text-xs font-medium shadow-sm border">
                      {trip.ambulanceId}
                    </div>
                  </div>
                ))}

                {/* Center Marker */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 sm:w-6 sm:h-6 bg-red-500 rounded-full border-2 border-white shadow-lg"></div>
                  <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Trip List */}
          {!hideActiveTrips && (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
                  Active Trips ({filteredTrips.length})
                </h4>
                <span className="text-xs sm:text-sm text-gray-500">
                  {activeTab ? `${tabs.find(t => t.id === activeTab)?.label}` : 'All Status'}
                </span>
              </div>
              
              <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
                {filteredTrips.map((trip) => (
                  <div
                    key={trip.id}
                    className={`p-3 sm:p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                      selectedTrip?.id === trip.id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedTrip(trip)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className={`w-6 h-6 sm:w-8 sm:h-8 ${getStatusColor(trip.status)} rounded-full flex items-center justify-center text-white`}>
                          {getStatusIcon(trip.status)}
                        </div>
                        <div>
                          <h5 className="text-xs sm:text-sm font-semibold text-gray-900">{trip.ambulanceId}</h5>
                          <p className="text-xs text-gray-600">{trip.driver}</p>
                        </div>
                      </div>
                      
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(trip.priority)}`}>
                        {trip.priority}
                      </span>
                    </div>
                    
                    <div className="space-y-1 sm:space-y-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <span className="text-xs sm:text-sm text-gray-600">{trip.patient}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Navigation className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <span className="text-xs sm:text-sm text-gray-600">{trip.destination}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <span className="text-xs sm:text-sm text-gray-600">ETA: {trip.eta}</span>
                      </div>
                      
                      {trip.patients > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <span className="text-xs sm:text-sm text-gray-600">{trip.patients} patient(s)</span>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        Started {trip.startTime} • Updated {trip.lastUpdate}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Selected Trip Details */}
        {selectedTrip && (
          <div className="mt-4 sm:mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                {selectedTrip.id} - Trip Details
              </h4>
              <button
                onClick={() => setSelectedTrip(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-xl">×</span>
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">Patient Information</h5>
                <p className="text-sm text-gray-600">{selectedTrip.patient}</p>
                <p className="text-xs text-gray-500">Pickup: {selectedTrip.pickupLocation}</p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">Driver</h5>
                <p className="text-sm text-gray-600">{selectedTrip.driver}</p>
                <p className="text-xs text-gray-500">Ambulance: {selectedTrip.ambulanceId}</p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">Current Status</h5>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 ${getStatusColor(selectedTrip.status)} rounded-full`}></div>
                  <span className="text-sm text-gray-600 capitalize">{selectedTrip.status.replace('_', ' ')}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">Destination</h5>
                <p className="text-sm text-gray-600">{selectedTrip.destination}</p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">ETA & Distance</h5>
                <p className="text-sm text-gray-600">{selectedTrip.eta}</p>
                <p className="text-xs text-gray-500">{selectedTrip.distance} remaining</p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-900">Priority</h5>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(selectedTrip.priority)}`}>
                  {selectedTrip.priority}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveMap;
